import type { z } from 'zod'

import { getEnv } from '@/config/env'
import { endSession } from '@/lib/auth/session-expiry'

import { errorEnvelopeSchema, messageEnvelopeSchema } from './envelope'
import { ApiError, ApiErrorCode, NetworkError, ResponseShapeError } from './errors'
import { amplifyTokenProvider, type TokenProvider } from './token-provider'

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export type QueryValue = string | number | boolean | readonly (string | number)[] | null | undefined

export type QueryParams = Record<string, QueryValue>

export interface RequestOptions<T> {
  schema: z.ZodType<T>
  method?: HttpMethod
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
  headers?: Readonly<Record<string, string>>

  auth?: boolean
}

export interface ApiClientOptions {
  baseUrl: string
  tokenProvider: TokenProvider

  onSessionExpired?: () => void | Promise<void>
  fetchImpl?: typeof fetch
}

type SendOptions<T> = Omit<RequestOptions<T>, 'schema' | 'query'>

function isAbort(error: unknown): boolean {
  return (
    typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError'
  )
}

function unwrapEnvelope(body: unknown): unknown {
  if (
    typeof body === 'object' &&
    body !== null &&
    'success' in body &&
    body.success === true &&
    'data' in body
  ) {
    return body.data
  }
  return body
}

export class ApiClient {
  readonly baseUrl: string
  private readonly tokenProvider: TokenProvider
  private readonly onSessionExpired: () => void | Promise<void>
  private readonly fetchImpl: typeof fetch

  constructor(options: ApiClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, '')
    this.tokenProvider = options.tokenProvider
    this.onSessionExpired = options.onSessionExpired ?? endSession
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis)
  }

  async request<T>(path: string, options: RequestOptions<T>): Promise<T> {
    const url = this.buildUrl(path, options.query)
    const sendOptions: SendOptions<T> = { ...options }

    let response = await this.send(url, path, sendOptions)

    if (response.status === 401 && options.auth !== false) {
      const refreshedToken = await this.tokenProvider.refreshAccessToken()

      if (refreshedToken !== null) {
        response = await this.send(url, path, sendOptions, refreshedToken)
      }

      if (response.status === 401) {
        const error = await this.toApiError(response, path)
        void this.onSessionExpired()
        throw error
      }
    }

    if (!response.ok) {
      const error = await this.toApiError(response, path)

      if (error.status === 403 && error.code === ApiErrorCode.ACCOUNT_DEACTIVATED) {
        void this.onSessionExpired()
      }

      throw error
    }

    return this.parseBody(response, path, options.schema)
  }

  get<T>(path: string, options: Omit<RequestOptions<T>, 'method' | 'body'>): Promise<T> {
    return this.request(path, { ...options, method: 'GET' })
  }

  post<T>(path: string, options: Omit<RequestOptions<T>, 'method'>): Promise<T> {
    return this.request(path, { ...options, method: 'POST' })
  }

  patch<T>(path: string, options: Omit<RequestOptions<T>, 'method'>): Promise<T> {
    return this.request(path, { ...options, method: 'PATCH' })
  }

  put<T>(path: string, options: Omit<RequestOptions<T>, 'method'>): Promise<T> {
    return this.request(path, { ...options, method: 'PUT' })
  }

  delete<T>(path: string, options: Omit<RequestOptions<T>, 'method'>): Promise<T> {
    return this.request(path, { ...options, method: 'DELETE' })
  }

  private buildUrl(path: string, query: QueryParams | undefined): string {
    if (!path.startsWith('/')) {
      throw new Error(`API path must start with "/": received "${path}"`)
    }

    const target = `${this.baseUrl}${path}`
    const url = target.startsWith('/')
      ? new URL(target, globalThis.location.origin)
      : new URL(target)

    for (const [key, value] of Object.entries(query ?? {})) {
      if (value === undefined || value === null) continue
      if (Array.isArray(value)) {
        for (const item of value) url.searchParams.append(key, String(item))
      } else {
        url.searchParams.set(key, String(value))
      }
    }

    return url.toString()
  }

  private async send<T>(
    url: string,
    path: string,
    options: SendOptions<T>,
    presetToken?: string,
  ): Promise<Response> {
    const headers = new Headers({ Accept: 'application/json' })

    for (const [key, value] of Object.entries(options.headers ?? {})) {
      headers.set(key, value)
    }

    if (options.body !== undefined) {
      headers.set('Content-Type', 'application/json')
    }

    if (options.auth !== false) {
      const token = presetToken ?? (await this.tokenProvider.getAccessToken())
      if (token !== null) {
        headers.set('Authorization', `Bearer ${token}`)
      }
    }

    const init: RequestInit = {
      method: options.method ?? 'GET',
      headers,
    }

    if (options.body !== undefined) {
      init.body = JSON.stringify(options.body)
    }

    if (options.signal) {
      init.signal = options.signal
    }

    try {
      return await this.fetchImpl(url, init)
    } catch (cause) {
      if (isAbort(cause)) throw cause
      throw new NetworkError(path, { cause })
    }
  }

  private async toApiError(response: Response, path: string): Promise<ApiError> {
    const raw: unknown = await response
      .clone()
      .json()
      .catch(() => undefined)

    const envelope = errorEnvelopeSchema.safeParse(raw)

    if (envelope.success) {
      return new ApiError({
        status: response.status,
        code: envelope.data.code,
        error: envelope.data.error,
        details: envelope.data.details,
        correlationId: envelope.data.correlationId,
        route: path,
      })
    }

    const message = messageEnvelopeSchema.safeParse(raw)

    if (message.success) {
      return new ApiError({
        status: response.status,
        code: `HTTP_${String(response.status)}`,
        error: message.data.message,
        route: path,
      })
    }

    return new ApiError({
      status: response.status,
      code: `HTTP_${String(response.status)}`,
      error: response.statusText || `Request to ${path} failed with ${String(response.status)}`,
      route: path,
    })
  }

  private async parseBody<T>(response: Response, path: string, schema: z.ZodType<T>): Promise<T> {
    const text = response.status === 204 ? '' : await response.text()
    let data: unknown

    if (text.trim() === '') {
      data = undefined
    } else {
      try {
        data = JSON.parse(text)
      } catch {
        throw new ResponseShapeError(path, ['Response body was not valid JSON'])
      }
    }

    const result = schema.safeParse(unwrapEnvelope(data))

    if (!result.success) {
      throw new ResponseShapeError(
        path,
        result.error.issues.map(
          (issue) => `${issue.path.length > 0 ? issue.path.join('.') : '(root)'}: ${issue.message}`,
        ),
      )
    }

    return result.data
  }
}

let defaultClient: ApiClient | undefined

export function apiClient(): ApiClient {
  defaultClient ??= new ApiClient({
    baseUrl: getEnv().VITE_API_BASE_URL,
    tokenProvider: amplifyTokenProvider,
  })
  return defaultClient
}

let aiPlaneClient: ApiClient | undefined

export function aiClient(): ApiClient {
  aiPlaneClient ??= new ApiClient({
    baseUrl: getEnv().VITE_AI_BASE_URL ?? '',
    tokenProvider: amplifyTokenProvider,
  })
  return aiPlaneClient
}
